import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileCard, type FileStatus } from '@/components/ui/file-card';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/utils/utils';

interface FileItem {
  fileName: string;
  id: string;
  status: FileStatus;
}

interface FilesUploadedListDropdownProps {
  containerRef?: React.RefObject<HTMLDivElement | null>;
  isUploadPending?: boolean;
  onClearAll?: () => void;
  onFileDelete?: (fileId: string) => void;
  onFileRetry?: (fileId: string) => void;
  onFilesChange?: (fileCount: number) => void;
  onFileView?: (fileId: string) => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  uploadedFiles?: Array<FileItem>;
}

export function FilesUploadedListDropdown({
  containerRef,
  isUploadPending = false,
  onClearAll,
  onFileDelete,
  onFileRetry,
  onFilesChange,
  onFileView,
  onOpenChange,
  open = false,
  uploadedFiles = [],
}: FilesUploadedListDropdownProps) {
  const { t } = useTranslation();
  const [popoverHeight, setPopoverHeight] = useState<number | undefined>(undefined);
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (open && containerRef?.current) {
      const updateDimensions = () => {
        if (containerRef?.current) {
          setPopoverHeight(containerRef.current.offsetHeight);
          // Get the container width for responsive dropdown
          const containerWidth = containerRef.current.offsetWidth;
          setPopoverWidth(containerWidth);
        }
      };

      updateDimensions();

      // Update on window resize
      window.addEventListener('resize', updateDimensions);
      return () => window.removeEventListener('resize', updateDimensions);
    }
    return undefined;
  }, [open, containerRef]);
  // Use uploadedFiles directly as the source of truth (fully controlled component)
  const files = uploadedFiles;

  // Notify parent of file count changes
  useEffect(() => {
    onFilesChange?.(files.length);
  }, [files.length, onFilesChange]);

  const handleView = (fileId: string) => {
    onFileView?.(fileId);
  };

  const handleDelete = (fileId: string) => {
    onFileDelete?.(fileId);
    onFilesChange?.(files.filter((f) => f.id !== fileId).length);
  };

  const handleCancel = (fileId: string) => {
    onFileDelete?.(fileId);
    onFilesChange?.(files.filter((f) => f.id !== fileId).length);
  };

  const handleRetry = (fileId: string) => {
    onFileRetry?.(fileId);
  };

  //same api-call to be made here
  const handleClearAll = () => {
    onFilesChange?.(0);
    onClearAll?.();
  };

  const hasProcessingFiles = files.some((f) => f.status === 'processing');
  const hasFailedFiles = files.some((f) => f.status === 'failed');
  const hasNoFiles = files.length === 0;
  const isClearAllDisabled = hasProcessingFiles || hasFailedFiles || isUploadPending || hasNoFiles;

  const dropdownContent = (
    <div className="bg-popover flex max-h-[280px] w-full flex-col overflow-hidden rounded-lg shadow-sm">
      {/* Header */}
      <div className="border-border-subtle flex items-center justify-between border-b px-3 py-1.5">
        <h3 className="text-tertiary font-['Inter'] text-[12px] leading-[160%] font-semibold tracking-[0%]">
          {t('assistant.tabs.filesUploaded')}
        </h3>
        <div
          className={cn(
            "inline-flex items-center justify-center font-['Inter'] text-[10px] leading-[160%] font-medium tracking-[0%] transition-colors",
            isClearAllDisabled
              ? 'cursor-not-allowed text-secondary opacity-40'
              : 'cursor-pointer text-secondary hover:opacity-80',
          )}
          onClick={isClearAllDisabled ? undefined : handleClearAll}
          role="button"
          tabIndex={isClearAllDisabled ? -1 : 0}
        >
          {t('assistant.files.clearAll')}
        </div>
      </div>

      {/* Files List */}
      <div
        className={`custom-scrollbar min-h-0 flex-1 overflow-y-auto ${files.length > 5 ? 'max-h-[180px]' : 'max-h-auto'}`}
      >
        <div className="flex flex-col">
          {files.length === 0 ? (
            <div className="px-3 py-3">
              <p className="text-foreground text-left font-['Inter'] text-sm font-medium">
                {t('assistant.files.none')}
              </p>
            </div>
          ) : (
            files.map((file) => {
              const props: {
                fileName: string;
                id: string;
                onCancel?: () => void;
                onDelete?: () => void;
                onRetry?: () => void;
                onView?: () => void;
                status: FileStatus;
              } = {
                fileName: file.fileName,
                id: file.id,
                status: file.status,
              };

              if (file.status === 'done') {
                props.onView = () => handleView(file.id);
                props.onDelete = () => handleDelete(file.id);
              } else if (file.status === 'processing') {
                props.onCancel = () => handleCancel(file.id);
              } else if (file.status === 'failed') {
                props.onRetry = () => handleRetry(file.id);
              }

              return <FileCard key={file.id} {...props} onView={() => handleView(file.id)} />;
            })
          )}
        </div>
      </div>
    </div>
  );

  // If open and onOpenChange are provided, wrap in Popover
  if (onOpenChange !== undefined) {
    return (
      <Popover onOpenChange={onOpenChange} open={open}>
        <PopoverAnchor asChild>
          <div className="absolute right-0 bottom-0 left-0 h-0" />
        </PopoverAnchor>
        {open && (
          <PopoverContent
            align="start"
            alignOffset={0}
            avoidCollisions={false}
            className="flex flex-col overflow-hidden rounded-none border-0 bg-transparent p-0 shadow-none data-[state=open]:animate-none"
            collisionPadding={0}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onEscapeKeyDown={() => onOpenChange?.(false)}
            onInteractOutside={(e) => e.preventDefault()}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            side="top"
            sideOffset={0}
            style={{
              maxWidth: '500px',
              width: popoverWidth ? `${popoverWidth}px` : '320px',
              ...(popoverHeight
                ? {
                    height: `${popoverHeight}px`,
                    maxHeight: `${popoverHeight}px`,
                  }
                : {}),
            }}
          >
            <div className="flex h-full min-h-0 w-full flex-col overflow-hidden px-3">
              {dropdownContent}
            </div>
          </PopoverContent>
        )}
      </Popover>
    );
  }

  // Otherwise, return the content directly
  return dropdownContent;
}
